import axiosNest from '../../api/axiosNest';

/**
 * Parametry podpowiadania aut — odpowiada `/suggestion-parameters` w API.
 *
 * Horyzont podpowiedzi, okno pozycji z historii zleceń, wiek odczytu GPS
 * i domyślna ładowność zestawu. Zakres i wartość domyślną trzyma API —
 * front ich nie powiela, tylko pokazuje to, co dostał.
 *
 * Obok progi alertu marży (`/suggestion-parameters/margin-thresholds`,
 * 01.10.2026): ta sama tabela i historia, ale próg wolno zostawić pusty —
 * pusty = alert wyłączony (tak od wdrożenia, dopóki Marathon nie poda liczb).
 */

export type ParameterChange = {
  id: number;
  key: string;
  oldValue: number | null;
  /** `null` = próg wyczyszczony. */
  newValue: number | null;
  reason: string;
  changedAt: string;
  changedById: number | null;
  changedByName: string | null;
  /**
   * Z przełącznikiem `parameterOwnership` (24.10.2026, S-24.10-12): rodzaj
   * wpisu — `value` (wartość), `owner` (właściciel, teksty w `oldText` /
   * `newText`), `scheduleCancelled` (odwołana zmiana zaplanowana) — i od
   * kiedy obowiązuje nowa wartość (`null` = od chwili zmiany).
   */
  field?: string;
  oldText?: string | null;
  newText?: string | null;
  validFrom?: string | null;
};

/** Rodzaje wpisów historii — dane z API. */
export const CHANGE_OWNER = 'owner';
export const CHANGE_SCHEDULE_CANCELLED = 'scheduleCancelled';

/** Przełącznik właściciela i daty obowiązywania — klucz z rejestru API. */
export const FEATURE_PARAMETER_OWNERSHIP = 'parameterOwnership';

/** Zmiana zaplanowana parametru (S-24.10-12). */
export type ScheduledChange = {
  /** `null` przy progu opcjonalnym = wyczyszczenie. */
  value: number | null;
  validFrom: string;
  byName: string | null;
  reason: string | null;
};

export type SuggestionParameter = {
  id: string;
  key: string;
  label: string;
  /** `null` wyłącznie przy progu opcjonalnym = nieustawiony. */
  value: number | null;
  unit: string;
  description: string | null;
  min: number;
  max: number;
  /** `null` = brak wartości domyślnej (próg opcjonalny). */
  defaultValue: number | null;
  /** Czy wolno zostawić pusty (próg alertu marży). Brak = nie. */
  optional?: boolean;
  /** Miejsca po przecinku dopuszczalne w wartości. Brak = 0. */
  decimals?: number;
  /**
   * Znaczenie zera spoza zakresu — np. „bez limitu” retencji pomiaru użycia
   * (decyzja użytkownika 23.10.2026). Brak = zero tylko w zakresie.
   */
  zeroMeans?: string;
  /** `false` — w bazie brak poprawnego wiersza, działa wartość domyślna z kodu. */
  configured: boolean;
  updatedAt: string | null;
  lastChange: ParameterChange | null;
  /**
   * Z przełącznikiem `parameterOwnership` (S-24.10-12): właściciel (`null`
   * = nieustalony), od kiedy obowiązuje bieżąca wartość i zmiana
   * zaplanowana. Bez przełącznika pól nie ma.
   */
  owner?: string | null;
  validFrom?: string | null;
  scheduled?: ScheduledChange | null;
};

export async function fetchParameters(): Promise<SuggestionParameter[]> {
  const { data } = await axiosNest.get<SuggestionParameter[]>('/suggestion-parameters');
  return data;
}

/** Progi alertu marży — osobna lista, ten sam zapis i ta sama historia. */
export async function fetchMarginThresholds(): Promise<SuggestionParameter[]> {
  const { data } = await axiosNest.get<SuggestionParameter[]>(
    '/suggestion-parameters/margin-thresholds',
  );
  return data;
}

/**
 * Granice pięciu pasm marży (paczka p. Mariusza 21.10, fala C, S-21.10-18) —
 * osobna lista, ten sam zapis i ta sama historia. Tylko przy przełączniku
 * `marginFiveBands` (wyłączony — API 404); zastępują wtedy progi alertu marży.
 */
export async function fetchMarginBands(): Promise<SuggestionParameter[]> {
  const { data } = await axiosNest.get<SuggestionParameter[]>('/suggestion-parameters/margin-bands');
  return data;
}

/** Przełącznik pięciu pasm marży — klucz z rejestru API (dane). */
export const FEATURE_MARGIN_FIVE_BANDS = 'marginFiveBands';

/**
 * Wagi i progi oceny pary zlecenie–pojazd 0–10 (13.10.2026) — osobna lista,
 * ten sam zapis i ta sama historia. Tylko przy przełączniku `pairScoring`
 * (wyłączony — API odpowiada 404).
 */
export async function fetchPairScoreParameters(): Promise<SuggestionParameter[]> {
  const { data } = await axiosNest.get<SuggestionParameter[]>('/suggestion-parameters/pair-score');
  return data;
}

/**
 * Progi alertów i założenia robocze (15.10.2026, Etap 1 makiety FTL —
 * PROMPT pkt 15) — osobna lista, ten sam zapis i ta sama historia. Tylko
 * przy przełączniku `alertThresholdParameters` (wyłączony — API 404).
 */
export async function fetchAlertThresholds(): Promise<SuggestionParameter[]> {
  const { data } = await axiosNest.get<SuggestionParameter[]>(
    '/suggestion-parameters/alert-thresholds',
  );
  return data;
}

/**
 * Region na pulpitach FTL per kraj (21.10.2026, `regionDigitsByCountry`) —
 * osobna lista: cyfry kodu dla krajów bez własnej wartości i wyjątki krajów.
 * Zapis tą samą trasą (`saveParameter`); nowy kraj — klucz
 * `regionDigitsKey(kraj)`. Wyłączony przełącznik — API 404.
 */
export async function fetchRegionDigits(): Promise<SuggestionParameter[]> {
  const { data } = await axiosNest.get<SuggestionParameter[]>('/suggestion-parameters/region-digits');
  return data;
}

/** Klucz wyjątku kraju — jak w API (`ftlRegionPostalDigits.<kraj ISO>`). */
export function regionDigitsKey(country: string): string {
  return `ftlRegionPostalDigits.${country.trim().toUpperCase()}`;
}

export async function fetchHistory(key?: string | null): Promise<ParameterChange[]> {
  const { data } = await axiosNest.get<ParameterChange[]>('/suggestion-parameters/history', {
    params: key ? { key } : {},
  });
  return data;
}

/** Czy zalogowany może zmieniać parametry — `GET /suggestion-parameters/access`. */
export type ParameterAccess = {
  canEdit: boolean;
  /** Nazwa uprawnienia, którego wymaga zapis (np. „Sekcja administracyjna”). */
  requiredPermission: string;
};

/**
 * Uprawnienie liczy API tą samą regułą co strażnik zapisu — front nie
 * powiela listy ról. To tylko podpowiedź dla ekranu; rozstrzyga zapis.
 */
export async function fetchAccess(): Promise<ParameterAccess> {
  const { data } = await axiosNest.get<ParameterAccess>('/suggestion-parameters/access');
  return data;
}

export async function saveParameter(
  key: string,
  value: number | null,
  reason: string,
  /**
   * Obowiązuje od (S-24.10-12) — chwila z przesunięciem; brak = od razu
   * (i bez pola w żądaniu, jak przed funkcją).
   */
  validFrom?: string | null,
): Promise<SuggestionParameter> {
  const { data } = await axiosNest.patch<SuggestionParameter>(
    `/suggestion-parameters/${encodeURIComponent(key)}`,
    validFrom ? { value, reason, validFrom } : { value, reason },
  );
  return data;
}

/** Właściciel parametru (S-24.10-12); `null` = nieustalony. */
export async function saveParameterOwner(
  key: string,
  owner: string | null,
  reason: string,
): Promise<SuggestionParameter> {
  const { data } = await axiosNest.patch<SuggestionParameter>(
    `/suggestion-parameters/${encodeURIComponent(key)}/owner`,
    { owner, reason },
  );
  return data;
}

/** Odwołanie zmiany zaplanowanej (S-24.10-12). */
export async function cancelScheduledParameter(
  key: string,
  reason: string,
): Promise<SuggestionParameter> {
  const { data } = await axiosNest.post<SuggestionParameter>(
    `/suggestion-parameters/${encodeURIComponent(key)}/schedule/cancel`,
    { reason },
  );
  return data;
}

/** Właściciel do pokazania — pusty to „właściciel nieustalony”, nie myślnik. */
export const ownerText = (owner: string | null | undefined): string =>
  owner && owner.trim() !== '' ? owner : 'właściciel nieustalony';

/**
 * Wartość z formularza. Zwraca liczbę albo komunikat — ta sama reguła co
 * w API (liczba całkowita w zakresie), żeby operator nie czekał na odmowę
 * serwera przy oczywistej literówce. Rozstrzyga i tak API.
 *
 * Próg opcjonalny (`optional`): puste pole = `null`, czyli wyczyszczenie
 * progu (alert wyłączony); `decimals` dopuszcza miejsca po przecinku.
 * Spójności progów między sobą nie sprawdzamy tutaj — mówi o niej API.
 */
export function parseParameterInput(
  raw: string,
  p: Pick<SuggestionParameter, 'label' | 'min' | 'max' | 'unit' | 'optional' | 'decimals' | 'zeroMeans'>,
): { value: number | null } | { error: string } {
  const text = raw.trim().replace(',', '.').replace('−', '-');
  if (text === '' && p.optional) return { value: null };
  const n = text === '' ? Number.NaN : Number(text);
  if (!Number.isFinite(n)) return { error: `${p.label}: wpisz liczbę (${p.unit})` };
  const decimals = p.decimals ?? 0;
  const scaled = n * 10 ** decimals;
  if (Math.abs(scaled - Math.round(scaled)) > 1e-9) {
    return {
      error:
        decimals === 0
          ? `${p.label}: wpisz liczbę całkowitą (${p.unit})`
          : `${p.label}: najwyżej ${decimals === 1 ? 'jedno miejsce' : `${decimals} miejsca`} po przecinku (${p.unit})`,
    };
  }
  if (n === 0 && p.zeroMeans) return { value: 0 };
  if (n < p.min || n > p.max) {
    return {
      error: `${p.label}: dopuszczalny zakres ${p.min}–${p.max} ${p.unit}${p.zeroMeans ? ` albo 0 (${p.zeroMeans})` : ''}`,
    };
  }
  return { value: n };
}

/**
 * Wartość do pokazania z nazwanym zerem („0 — bez limitu…”); inaczej jak
 * `formatValue`.
 */
export const formatParameterValue = (
  v: number | null | undefined,
  p: Pick<SuggestionParameter, 'unit' | 'zeroMeans'>,
): string => (v === 0 && p.zeroMeans ? `0 — ${p.zeroMeans}` : formatValue(v, p.unit));

/** Pusty próg opcjonalny — alert wyłączony, nie zero. */
export const EMPTY_THRESHOLD = 'pusty — alert wyłączony';

/** Wartość z jednostką; brak (np. stara wartość przed pierwszym wierszem) to myślnik. */
export const formatValue = (v: number | null | undefined, unit: string): string =>
  v === null || v === undefined ? '—' : `${v.toLocaleString('pl-PL')} ${unit}`;
